/* eslint @typescript-eslint/no-var-requires: "off" */
/* eslint-disable react/prop-types */
import React, { useRef } from "react";
import { Coverage, CoverageItem, InsuranceCoverageState } from "../store/Insurance";
import { Typography, SvgIcon } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";
import clsx from "clsx";
import SelfRiskSelector from "./SelfRiskSelector";
import { GetIntFromString } from "../helperFunctions";
import { Theme } from "../.themes";
import { useWindowSize } from "../hooks";
import PriceTag from "./PriceTag";
import Popup from "./Popup";
import { BrandsEnum } from "../enums";
import { AddOnState } from "../store/AddOn";
import GlassDeductibleSelector from "./GlassDeductibleSelector";
import AvatarCoverageCard from "./AvatarCoverageCard";
import CoverageCard from "./CoverageCard";
import { useStyles } from "./InsuranceCoverageSelectBase.style";

/* @typescript-eslint/no-var-requires  */
const useResizeAware = require("react-resize-aware");

type CoverageColumnProps = {
  coverageItems: CoverageItem[];
  coverage: Coverage;
  recommendedTitle?: string;
  selectedCoverageCode?: string;
  showSecondaryPrice?: boolean;
  onClick: Function;
  setActiveColumnRef: (elem: HTMLDivElement | null) => void;
  isSingleRecommended: boolean;
  isCarUnder3YearsAndNotImported: boolean;
  addons?: AddOnState;
  showAvatar: boolean | undefined;
  forwardedRef?: any;
};

type CoverageColumnItemProps = {
  coverage: Coverage;
  elem: CoverageItem;
  isSelected: boolean;
  showAvatar: boolean | undefined;
};

export const arrowSize = (theme: Theme) => {
  return theme.spacing(3);
};

const emptyHtmlChar = <span>&#8203;</span>;

const CoverageColumnItem: React.FC<CoverageColumnItemProps> = (props: CoverageColumnItemProps) => {
  const { coverage, elem, isSelected } = props; // extract props

  const theme: Theme = useTheme();
  const classes = useStyles(theme);
  const included = coverage.CoverageItems.find(i => i.InternalCode === elem.InternalCode) ? true : false;
  const classNames = clsx(classes.CoverageItemIcon, included && "included");
  const hasDescription = !elem.Description ? false : true;

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const check = (theme.icons?.checkmarkIcon && <SvgIcon className={classNames}>{theme.icons?.checkmarkIcon}</SvgIcon>) ?? (
    <CheckIcon className={classNames} />
  );
  const cross = (theme.icons?.crossIcon && <SvgIcon className={classNames}>{theme.icons?.crossIcon}</SvgIcon>) ?? (
    <ClearIcon className={classNames} />
  );

  const rowContent = (
    <span
      onClick={
        isSelected
          ? openPopover
          : () => {
              /* no action required */
            }
      }
      className={clsx(classes.CoverageItemText, hasDescription && "withDescription")}
    >
      {included ? check : cross}
      {isSelected ? elem.Name : null}
    </span>
  );

  return (
    <li className={classes.CoverageItemRow}>
      {rowContent}
      <Popup
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        description={elem.Description}
        footerDescription={elem.ItemDeductible ? "Självrisk : " + elem.ItemDeductible : undefined}
        title={elem.Name}
      />
    </li>
  );
};

const CoverageColumn: React.FC<CoverageColumnProps> = (props: CoverageColumnProps) => {
  const classes = useStyles();

  const primaryPrice = props.coverage.PrimaryPrice;
  let secondaryPriceText: string | undefined;

  if (props.showSecondaryPrice) {
    secondaryPriceText = props.coverage.CalculatedPriceSecondary;
  }

  const recommendedTitle = props.coverage.IsRecommended ? props.recommendedTitle ?? "Vi rekommendar" : emptyHtmlChar;

  const isSelected = props.selectedCoverageCode === props.coverage.InternalCode;
  const coverageName = isSelected ? props.coverage.LongName : props.coverage.ShortName;

  const inactiveTabPrice = `${GetIntFromString(props.coverage.CalculatedPrice)} ${primaryPrice.Currency}`;

  function handleOnClick(newId: string, handler: Function) {
    if (handler && !isSelected) {
      handler(newId);
    }
  }

  function renderHeader() {
    return <span className={classes.CoverageHeaderName}>{coverageName}</span>;
  }

  function renderPriceTag() {
    const contentAlign = "center";
    return isSelected ? (
      <PriceTag
        priceText={props.coverage.CalculatedPrice}
        justifyContent={contentAlign}
        fontWeight={700}
        textClassName={classes.CoverageHeaderPrice}
      ></PriceTag>
    ) : (
      <PriceTag priceText={inactiveTabPrice} justifyContent={contentAlign} textClassName={classes.CoverageHeaderPrice}></PriceTag>
    );
  }

  function renderCurrency() {
    return (
      !props.isSingleRecommended &&
      secondaryPriceText && (
        <span className={classes.CoverageHeaderName}>
          {isSelected ? secondaryPriceText : `${GetIntFromString(secondaryPriceText)} ${primaryPrice.Currency}`}
        </span>
      )
    );
  }

  function renderRecommendedTitleInCoverageTable() {
    return (
      isSelected && (
        <div className={classes.CoverageRecommended}>
          {!props.showAvatar && (
            <Typography className={classes.CoverageRecommended} variant='caption'>
              {recommendedTitle}
            </Typography>
          )}
        </div>
      )
    );
  }

  function renderCoverageHeader() {
    const result = (
      <div className={classes.CoverageHeader}>
        {renderHeader()}
        {renderPriceTag()}
        {renderCurrency()}
      </div>
    );

    return result;
  }

  return (
    <div
      tabIndex={0}
      className={
        props.isSingleRecommended && props.isCarUnder3YearsAndNotImported
          ? clsx(classes.SingleRecommendedColumn)
          : clsx(classes.CoverageColumnWrapper, isSelected && "selected")
      }
      onClick={() => handleOnClick(props.coverage.InternalCode, props.onClick)}
      onKeyDown={e => {
        if (e.key === "Enter") handleOnClick(props.coverage.InternalCode, props.onClick);
      }}
      ref={el => {
        // el can be null - see https://reactjs.org/docs/refs-and-the-dom.html#caveats-with-callback-refs
        if (!el) return;

        if (isSelected) {
          props.setActiveColumnRef(el);
          if (props.forwardedRef?.current) props.forwardedRef.current = el;
        }
      }}
    >
      {renderRecommendedTitleInCoverageTable()}
      <div className={classes.CoverageColumn}>
        {renderCoverageHeader()}
        <ul className={classes.CoverageItemList}>
          {props.coverageItems.map((elem, i) => (
            <CoverageColumnItem elem={elem} key={i} coverage={props.coverage} isSelected={isSelected} showAvatar={props.showAvatar} />
          ))}
        </ul>
      </div>
    </div>
  );
};

export const InsuranceCoverageSelectBase = (props: InsuranceCoverageProps) => {
  const theme: Theme = useTheme();
  const classes = useStyles(theme);

  const coverages = props;
  coverages.CoverageItems?.sort((a, b) => a.SortOrder - b.SortOrder);
  coverages.Coverages?.sort((a, b) => a.SortOrder - b.SortOrder);

  let showSecondaryPrice = false;
  if (props.showSecondaryPrice) {
    switch (theme.brand) {
      case BrandsEnum.Bmw:
      case BrandsEnum.Mini:
        showSecondaryPrice = true;
        break;
    }
  }

  const [coverageTableListener] = useResizeAware();

  const showSingleRecommendedTable = coverages.IsSingleRecommened && coverages.isCarUnder3YearsAndNotImported;

  const currentCoverage = coverages.Coverages?.find(elem => elem.InternalCode === coverages.selectedCode);

  const [arrowStyle, setArrowStyle] = React.useState<{}>({
    left: `${theme.spacing(1)}px`,
  });

  const cardRef = useRef<HTMLSpanElement>(null);

  const [tableScroll, setTableScroll] = React.useState<number | null>();
  const [selectedColumnRef, setSelectedColumnRef] = React.useState<HTMLDivElement | null>(null);
  const [tableRef, setTableRef] = React.useState<HTMLDivElement | null>(null);

  const windowSize = useWindowSize(); // to re-position arrow on window width change

  React.useMemo(() => {
    if (selectedColumnRef != null && tableRef != null) {
      const rect = selectedColumnRef.getBoundingClientRect();
      const tableOffset = tableRef.offsetLeft;

      let columnPos = rect.left - tableOffset + rect.width / 2;

      if (cardRef?.current?.getBoundingClientRect()) {
        const cardRect = cardRef?.current?.getBoundingClientRect();
        const arrowWidth = arrowSize(theme);
        const arrowHalf = arrowWidth / 2;

        const rightMargin = cardRect.right - (arrowWidth + arrowHalf);
        const leftMargin = cardRect.left - tableOffset;

        // handle arrow reaching end of card - pin to end
        if (columnPos > rightMargin) {
          columnPos = rightMargin;
        }
        if (columnPos < leftMargin) {
          columnPos = leftMargin;
        }

        // handle arrow going out of card bounds
        if (rightMargin + arrowHalf < rect.left || leftMargin > rect.right - arrowWidth) {
          setArrowStyle({
            display: "none",
          });
          return;
        }
      }

      setArrowStyle({
        left: `${columnPos}px`,
      });
    } else {
      setArrowStyle({
        display: "none",
      });
    }
    /* eslint-disable */
  }, [selectedColumnRef, tableScroll, cardRef, tableRef, theme, windowSize]);
  /* eslint-enable */

  React.useMemo(() => {
    function handleScroll() {
      setTableScroll(tableRef?.scrollLeft);
    }

    handleScroll();
    tableRef?.addEventListener("scroll", handleScroll);
  }, [tableRef]);

  // function renderRecommendedTitleBeforeCoverageCard() {
  //   return (
  //     isPolestar && (
  //       <Typography className={classes.RecommendedCoverageDescriptionHeader} variant='h2'>
  //         {recommendedTitle}
  //       </Typography>
  //     )
  //   );
  // }

  return (
    <div className={classes.ComponentWrapper} id='ws-coverageSelector'>
      {coverages.ui?.coverageSelect?.label && <Typography variant='h2'>{coverages.ui.coverageSelect.label}</Typography>}
      {/* {renderRecommendedTitleBeforeCoverageCard()} */}
      {currentCoverage ? (
        props.showAvatar ? (
          <AvatarCoverageCard
            arrowStyle={arrowStyle}
            cardRef={cardRef}
            currentCoverage={currentCoverage}
            recommendedTitle={coverages.recommendedTitle}
            showAvatar={props.showAvatar}
          />
        ) : (
          <CoverageCard
            arrowStyle={arrowStyle}
            cardRef={cardRef}
            currentCoverage={currentCoverage}
            recommendedTitle={coverages.recommendedTitle}
          />
        )
      ) : null}

      {/* This code is for SingleRecommended Coverage and will be wtsted on A/B testing - it might be deleted if testing fails or refactoring will be needed */}
      {showSingleRecommendedTable ? (
        <div
          className={classes.SingleRecommendation}
          ref={el => {
            if (!el) return;

            setTableRef(el);
          }}
        >
          {coverageTableListener}
          {coverages.Coverages?.map(
            (elem, i) =>
              elem.IsRecommended && (
                <CoverageColumn
                  key={i}
                  coverage={elem}
                  coverageItems={coverages.CoverageItems ?? []}
                  recommendedTitle={coverages.recommendedTitle}
                  selectedCoverageCode={coverages.selectedCode}
                  onClick={props.onChange}
                  showSecondaryPrice={showSecondaryPrice}
                  setActiveColumnRef={setSelectedColumnRef}
                  isSingleRecommended={coverages.IsSingleRecommened ?? false}
                  isCarUnder3YearsAndNotImported={coverages.isCarUnder3YearsAndNotImported ?? false}
                  addons={props.addons}
                  showAvatar={props.showAvatar}
                  forwardedRef={props.forwardedRef}
                />
              ),
          )}
        </div>
      ) : (
        <div
          className={classes.TableWrapper}
          ref={el => {
            // el can be null - see https://reactjs.org/docs/refs-and-the-dom.html#caveats-with-callback-refs
            if (!el) return;

            setTableRef(el);
          }}
        >
          {coverageTableListener}
          {coverages.Coverages?.map((elem, i) => {
            return (
              <CoverageColumn
                key={i}
                coverage={elem}
                coverageItems={coverages.CoverageItems ?? []}
                recommendedTitle={coverages.recommendedTitle}
                selectedCoverageCode={coverages.selectedCode}
                onClick={props.onChange}
                showSecondaryPrice={showSecondaryPrice}
                setActiveColumnRef={setSelectedColumnRef}
                isSingleRecommended={coverages.IsSingleRecommened ?? false}
                isCarUnder3YearsAndNotImported={coverages.isCarUnder3YearsAndNotImported ?? false}
                addons={props.addons}
                showAvatar={props.showAvatar}
                forwardedRef={props.forwardedRef}
              />
            );
          })}
        </div>
      )}
      {currentCoverage?.Deductibles && (
        <div ref={props.deductibleRef} className={classes.SelfRiskWrapper}>
          {props.selfRiskSelector ?? <SelfRiskSelector />}
        </div>
      )}
      {currentCoverage?.GlassDeductibles && (
        <div className={classes.SelfRiskWrapper}>{props.glassDeductibleSelector ?? <GlassDeductibleSelector />}</div>
      )}
    </div>
  );
};

type InsuranceCoverageProps = InsuranceCoverageState & {
  addons?: AddOnState;
  showSecondaryPrice?: boolean;
  showAvatar?: boolean;
  onChange: Function;
  selfRiskSelector?: React.ReactNode;
  glassDeductibleSelector?: React.ReactNode;
  forwardedRef?: any;
  deductibleRef?: any;
};

export default InsuranceCoverageSelectBase;
